import { inject, Injectable } from '@angular/core';
import {
    CreateRelationDialogOutput,
    CreateRelationDialogOutputSuccess
} from '@common/entities/dialog/create-relation-dialog.entity';
import { CreateRelationDialogComponent } from '@common/modules/dialog/create-relation-dialog/create-relation-dialog.component';
import { RecordsSandbox } from '@common/modules/records/sandbox/records.sandbox.service';
import { ActionItemOptions, UserAction } from '@mediahaven/shared';
import { ApiFragment, DialogRef, Mh2Entity, NavigationLink, ShDynamicDialogService } from '@stylehaven/components';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { MediaHavenActionType } from '../../../entities/action-types.entity';
import { ActionService } from '../../abstract/action.service.interface';
import { OpenEditActionService } from '../open-edit/open-edit.action.service';

@Injectable({ providedIn: 'root' })
export class CreateRelationActionService extends ActionService {
    protected readonly action: MediaHavenActionType = MediaHavenActionType.CREATE_RELATION;

    // Services
    private readonly dialogService: ShDynamicDialogService = inject(ShDynamicDialogService);
    private readonly recordSandbox: RecordsSandbox = inject(RecordsSandbox);
    private readonly editActionService: OpenEditActionService = inject(OpenEditActionService);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = true;
    protected readonly DISABLE_ON_EXECUTE: boolean = true;

    /* Edit
     * Available when:
     *  * User has edit rights
     *  * Item is NOT deleted
     *  * Item is not a representation
     *
     * Disabled when:
     *   * Has non-editable status
     *   * Is a collection with candidate children
     *   * Item has errors
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return of(this.userActionBuilder(record));
    }

    public userActionBuilder(record: ApiFragment): UserAction {
        return this.editActionService.userActionBuilder(record);
    }

    protected onAction(record: ApiFragment, actionItemOption: ActionItemOptions): Observable<boolean> {
        const title: string = this.translate('actions.relation.create');
        const create: string = this.translate('actions.save');
        const cancel: string = this.translate('actions.common.cancel');
        const ref: DialogRef<CreateRelationDialogOutput> = this.dialogService.open(CreateRelationDialogComponent, {
            title,
            data: { create, cancel, isCollection: record.isNewCollection(), fragmentId: record.getFragmentId() }
        });
        const dialog$: Observable<CreateRelationDialogOutputSuccess> = ref.afterClosed$.pipe(
            filter(val => !!val),
            filter(
                (val: CreateRelationDialogOutput): val is CreateRelationDialogOutputSuccess =>
                    val.type === 'success' && val.hasOwnProperty('relationType') && val.hasOwnProperty('fragmentId')
            )
        );

        return dialog$.pipe(
            tap(() => this.initBusyToast(record, actionItemOption, true)),
            switchMap(output => this.recordSandbox.createRelation(record, output.fragmentId, output.relationType)),
            map(() => true)
        );
    }

    public getLabelKey(target: ApiFragment): string {
        return 'actions.relation.create';
    }

    public getToastLabelKey(target: ApiFragment): string {
        return 'action-toasts.create-relation';
    }

    public getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return target.getMh2Entity();
    }

    public getSuccessToastLink(): NavigationLink | undefined {
        return undefined;
    }

    public getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return '';
    }

    public getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {};
    }
}
